<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid pt-0>
            <v-row align="center">
              <div>
                <h1 class="headline">Agents</h1>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find agent"
                  searchButtonId="findAgentBtn"
                  ref-name="agentListFilters"
                  @searchParamsChanged="searchParamsChanged"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div>
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                  :ref-name="refNameColumns"
                ></list-visible-columns-and-filter>
              </div>
              <div>
                <v-tooltip
                  v-if="$store.getters.hasAccessTo('agents:create_agent')"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      class="ml-2"
                      fab
                      dark
                      color="primary"
                      @click="onCreateNew"
                    >
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>Create New Agent</span>
                </v-tooltip>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>

      <v-card>
        <v-layout fluid>
          <v-flex xs12>
            <div v-if="showAgGrid" key="ag-grid">
              <ag-grid-vue
                class="ag-theme-alpine"
                id="ag-agent-list"
                :columnDefs="agGridColumns"
                :rowData="items"
                rowSelection="multiple"
                :context="agContext"
                @grid-ready="onAgGridReady"
                @columnMoved="onAgColumnMoved"
                @columnResized="onAgColumnResized"
                @sortChanged="onAgSortChanged"
                @selection-changed="onAgSelectionChanged"
                @newColumnsLoaded="onAgNewColumns"
                suppressDragLeaveHidesColumns
              >
              </ag-grid-vue>
            </div>
            <v-data-table
              v-else
              :headers="filteredHeaders"
              :items="items"
              :loading="$apollo.loading"
              v-bind="pagingOptions"
              :search="search"
              item-key="id"
              class="elevation-1"
              fixed-header
              show-select
              v-model="selected"
              hide-default-footer
              @update:options="onListOptionsUpdate"
              :height="gridHeight"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
            >
              <template v-slot:item.notInUse="{ item }">
                <v-checkbox
                  v-model="item.notInUse"
                  primary
                  hide-details
                  :disabled="true"
                  class="ma-0"
                ></v-checkbox>
              </template>
              <template v-slot:item.language="{ item }">
                <span>{{ item.language ? item.language.name : "" }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <default-list-actions
                  :item="item"
                  :hide-view="!showView"
                  :itemName="'agent'"
                  :hide-edit="false"
                  :hide-remove="hideRemove"
                  @deleteItem="deleteItem($event)"
                  @viewItem="editItem($event)"
                  @editItem="editItem($event)"
                >
                  <template slot="extraButton">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-if="showClone"
                          v-on="on"
                          icon
                          small
                          class="mx-0"
                          @click="cloneItem(item)"
                        >
                          <v-icon color="primary" small
                            >mdi-content-copy</v-icon
                          >
                        </v-btn>
                      </template>
                      <span>Copy</span>
                    </v-tooltip>
                  </template>
                </default-list-actions>
              </template>
              <template v-slot:footer>
                <list-actions
                  v-if="selected.length > 0"
                  :selected="selected"
                  list="users"
                  :columns="visibleColumns"
                  :filter="searchParams"
                  @close="selected = []"
                  :hide-export="!$store.getters.hasAccessTo('agents:export')"
                >
                </list-actions>
                <v-row justify="center" v-if="selected.length > 0">
                  <h3 class="py-2 caption">
                    {{ selected.length }} record(s) selected
                  </h3>
                </v-row>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-card>
      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="agents"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('agents')"
      ></get-more-results>
    </v-flex>
    <clone-agent
      :show="showCloneAgent"
      v-model="selectedAgent"
      @close="showCloneAgent = false"
      @complete="onCloneComplete"
    ></clone-agent>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../mixins/ListMixin";
import AgGridListMixin from "../../../mixins/AgGridListMixin";
import AgGridSwitch from "../shared/AgGridSwitch.vue";
import { AgGridVue } from "ag-grid-vue";
import {
  ALL_AGENTS_SEARCH_QUERY,
  AGENT_DELETE_MUTATION,
  ALL_AGENTS_WITH_CURRENCY,
} from "../../../constants/graphql";
import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import ListFilters from "../shared/list-filters/ListFilters";
import ListActions from "../shared/list-actions/ListActions";
import agentsFilters from "./agents-filters";
import { removeGQLTypename } from "../../../helpers";
import CloneAgent from "./CloneAgent";

export default {
  name: "AgentList",

  props: {
    refName: {
      type: String,
      default: () => "agent-list",
    },
    refNameColumns: {
      type: String,
      default: () => "agent-list-cols",
    },
  },

  mixins: [ListMixin, AgGridListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListMixin,
    ListVisibleColumnsAndFilter,
    ListFilters,
    CloneAgent,
    ListActions,
    AgGridSwitch,
    AgGridVue,
  },

  data() {
    return {
      agents: {},
      selected: [],
      searchParams: { not_in_use_eq: false },
      agentsFilters: agentsFilters,
      showCloneAgent: false,
      selectedAgent: null,
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "name",
        "companyCode",
        "department",
        "email",
        "actions",
      ]),
      headers: [
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Company Code",
          value: "companyCode",
          name: "companyCode",
        },
        {
          text: "Agent Type",
          value: "agentType",
          name: "agentType",
        },
        {
          text: "Department",
          value: "department",
          name: "department",
        },
        {
          text: "Email",
          value: "email",
          name: "email",
        },
        {
          text: "Address",
          value: "address",
          name: "address",
        },
        {
          text: "City",
          value: "city",
          name: "city",
        },
        {
          text: "State",
          value: "state",
          name: "state",
        },
        {
          text: "Region",
          value: "region",
          name: "region",
        },
        {
          text: "Zip",
          value: "postCode",
          name: "postCode",
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
        },
        {
          text: "Not In Use",
          value: "notInUse",
          name: "notInUse",
        },
        {
          text: "Telephone",
          value: "telephone",
          name: "telephone",
        },
        {
          text: "Language",
          value: "language",
          name: "language",
          valueGetter: (payload) =>
            payload.data.language ? payload.data.language.name : "",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              showClone: true,
            },
          }),
        },
      ],
    };
  },

  computed: {
    items() {
      return this.agents && this.agents.edges
        ? this.agents.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return agentsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    showView() {
      return this.$store.getters.hasAccessTo("agents:view_only");
    },
    hideEdit() {
      return (
        !this.$store.getters.hasAccessTo("agents:edit_agent") ||
        this.$store.getters.hasAccessTo("agents:view_only")
      );
    },
    hideRemove() {
      return !this.$store.getters.hasAccessTo("agents:delete_agent");
    },
    showClone() {
      return (
        this.$store.getters.hasAccessTo("administration_panel:user_copy") &&
        this.$store.getters.hasAccessTo("agents:edit_user") &&
        !this.$store.getters.hasAccessTo("agents:view_only")
      );
    },
  },

  methods: {
    cloneItem(item) {
      this.selectedAgent = { ...item };
      this.showCloneAgent = true;
    },
    onCloneComplete(payload) {
      this.$store.dispatch("showAppSnackbar", {
        msg: `${payload.name} Created.`,
      });
      this.showCloneAgent = false;
      if (this.$apollo.queries.agents != null) {
        this.$apollo.queries.agents.refetch();
      }
    },
    editItem(item) {
      this.$router.push("/agent/" + item.id + "/edit");
    },
    onCreateNew() {
      this.$router.push("/agent/new");
    },

    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: AGENT_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Agent removed." });
      this.$apollo.queries.agentsWithCurrencies.refetch(); //http://tracker.globetrack.ie/issues/5084
      if (this.$apollo.queries.agents != null) {
        this.$apollo.queries.agents.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
  },

  apollo: {
    agentsWithCurrencies: {
      //http://tracker.globetrack.ie/issues/5084
      query: ALL_AGENTS_WITH_CURRENCY,
      update: (data) => data.agents,
    },
    agents: {
      query: ALL_AGENTS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: this.searchParams,
          sort: this.sort,
          first: this.first,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.agents.totalCount;
      },
    },
  },
};
</script>
<style>
.ag-body-viewport-wrapper.ag-layout-normal {
  overflow-x: scroll;
  overflow-y: scroll;
}
#ag-agent-list {
  height: calc(100vh - 220px);
}
</style>


// WEBPACK FOOTER //
// src/components/gt/agents/AgentList.vue